import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ratingsService } from '../../services/ratingsService';
import { RatingResponse } from '../../types/ratings';
import './Page.css';

const Reviews: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const TEST_PHONE_NUMBER = process.env.REACT_APP_TEST_PHONE_NUMBER || '+998930000000';

  const [ratings, setRatings] = useState<RatingResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const pageSize = 20;

  // Read from URL params or use defaults
  const currentPage = parseInt(searchParams.get('page') || '0', 10);

  // Helper function to update URL params
  const updatePage = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  useEffect(() => {
    fetchRatings();
  }, [currentPage]);

  const fetchRatings = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await ratingsService.getRatings({
        page: currentPage,
        pageSize,
      });

      if (response.success && response.data) {
        setRatings(response.data.ratings);
        setTotalCount(response.data.totalCount);
      } else {
        setError(response.error || 'Failed to fetch ratings');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (orderId: number) => {
    navigate(`/order-details/${orderId}`);
  };

  const handleDelete = async (event: React.MouseEvent, rating: RatingResponse) => {
    event.stopPropagation();

    if (!window.confirm(t('reviews.deleteConfirm'))) {
      return;
    }

    setDeletingId(rating.id);
    setError('');
    setSuccessMessage('');

    try {
      const response = await ratingsService.deleteRating(rating.orderId);

      if (response.success) {
        await fetchRatings();
        setSuccessMessage(t('reviews.deleteSuccess'));
      } else {
        setError(response.error || t('reviews.deleteFailed'));
      }
    } catch (error) {
      setError(t('reviews.deleteFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} style={{ color: i <= rating ? '#FFD700' : '#ddd', fontSize: '1.2rem' }}>
          ★
        </span>
      );
    }
    return <div style={{ display: 'flex', gap: '2px' }}>{stars}</div>;
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>{t('reviews.title')}</h1>
        <p>{t('reviews.subtitle')}</p>
      </div>

      <div className="page-content">
        {/* Stats Section */}
        {!loading && ratings.length > 0 && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <span style={{ fontSize: '0.9rem', color: '#666' }}>{t('reviews.totalReviews')}</span>
              <span style={{ fontSize: '1.5rem', fontWeight: 'bold', marginLeft: '0.5rem' }}>{totalCount}</span>
            </div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              {t('reviews.page')} {currentPage + 1} {t('reviews.of')} {Math.ceil(totalCount / pageSize)}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}
        {successMessage && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            marginBottom: '1.5rem'
          }}>
            {successMessage}
          </div>
        )}

        {/* Reviews Table */}
        <div className="table-container">
          {loading ? (
            <div className="loading-container">{t('reviews.loadingReviews')}</div>
          ) : ratings.length === 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('reviews.orderId')}</th>
                  <th>{t('reviews.client')}</th>
                  <th>{t('reviews.restaurant')}</th>
                  <th>{t('reviews.driver')}</th>
                  <th>{t('reviews.service')}</th>
                  <th>{t('reviews.createdAt')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={7} className="no-data">
                    {t('reviews.noReviews')}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('reviews.orderId')}</th>
                  <th>{t('reviews.client')}</th>
                  <th>{t('reviews.restaurant')}</th>
                  <th>{t('reviews.driver')}</th>
                  <th>{t('reviews.service')}</th>
                  <th>{t('reviews.createdAt')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {ratings.map((rating) => (
                  <tr
                    key={rating.id}
                    onClick={() => handleRowClick(rating.orderId)}
                    className="clickable-row"
                  >
                    <td>
                      <strong>#{rating.orderId}</strong>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span>{rating.clientName || <span style={{ color: '#999' }}>No name</span>}</span>
                        <span style={{ fontSize: '0.85rem', color: '#666' }}>{rating.clientPhone}</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {renderStars(rating.restaurantRating)}
                        {rating.restaurantNote && (
                          <span style={{ fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
                            "{rating.restaurantNote.length > 30
                              ? rating.restaurantNote.substring(0, 30) + '...'
                              : rating.restaurantNote}"
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {renderStars(rating.driverRating)}
                        {rating.driverNote && (
                          <span style={{ fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
                            "{rating.driverNote.length > 30
                              ? rating.driverNote.substring(0, 30) + '...'
                              : rating.driverNote}"
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {renderStars(rating.serviceRating)}
                        {rating.serviceNote && (
                          <span style={{ fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
                            "{rating.serviceNote.length > 30
                              ? rating.serviceNote.substring(0, 30) + '...'
                              : rating.serviceNote}"
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{formatDate(rating.createdAt)}</td>
                    <td>
                      {rating.clientPhone === TEST_PHONE_NUMBER ? (
                        <button
                          onClick={(event) => handleDelete(event, rating)}
                          disabled={deletingId === rating.id}
                          style={{
                            padding: '0.5rem 0.9rem',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: deletingId === rating.id ? 'not-allowed' : 'pointer',
                            opacity: deletingId === rating.id ? 0.6 : 1,
                            fontSize: '0.85rem'
                          }}
                          title={t('reviews.deleteOnlyTest')}
                        >
                          {deletingId === rating.id ? t('reviews.deleting') : t('common.delete')}
                        </button>
                      ) : (
                        <span style={{ color: '#999', fontSize: '0.85rem' }}>
                          {t('reviews.deleteOnlyTest')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && ratings.length > 0 && (
          <div className="pagination">
            <button
              className="pagination-button"
              onClick={() => updatePage(currentPage - 1)}
              disabled={currentPage === 0}
            >
              {t('common.previous')}
            </button>
            <span className="pagination-info">{t('orders.page')} {currentPage + 1}</span>
            <button
              className="pagination-button"
              onClick={() => updatePage(currentPage + 1)}
              disabled={ratings.length < pageSize}
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reviews;
